import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, Server } from 'lucide-react';
import SEO from '../components/SEO';

export default function BlogDataCenterPlanning() {
  const canonical = 'https://divaisetech.com/blog/data-center-hardware-planning-guide';
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPage',
    name: 'Data Center Hardware Planning: Complete Procurement & Capacity Guide',
    url: canonical,
    headline: 'Data Center Hardware Planning: Complete Procurement & Capacity Guide',
    description: 'Complete guide to data center hardware planning including capacity modeling, vendor selection, colocation, and infrastructure procurement.',
    datePublished: '2025-01-01',
    author: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://divaisetech.com/logo.png',
      },
    },
    mainEntityOfPage: canonical,
  };

  return (
    <div>
      <SEO
        title="Data Center Hardware Planning Guide | Infrastructure Capacity Planning"
        description="Complete guide to data center hardware planning including capacity modeling, vendor selection, colocation, and infrastructure procurement."
        keywords="data center hardware, capacity planning, servers, storage, networking, colocation, infrastructure planning"
        canonical={canonical}
        schema={articleSchema}
      />

      {/* Blog Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-block bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Infrastructure Guide
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Data Center Hardware Planning: Complete Procurement & Capacity Guide</h1>
          <p className="text-xl text-slate-200 mb-8">
            Strategic framework for planning data center hardware, capacity modeling, vendor selection, and cost optimization for enterprise infrastructure.
          </p>
          <div className="flex flex-wrap gap-6 text-slate-300">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>January 2025</span>
            </div>
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>By Infrastructure Architects, Divaise</span>
            </div>
            <div>
              <span>14 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <article className="py-16 px-6 bg-black">
        <div className="max-w-3xl mx-auto bg-black rounded-2xl p-8 md:p-10 border border-gray-800 [&_h2]:!text-white [&_h3]:!text-white [&_h4]:!text-white [&_p]:!text-slate-300 [&_li]:!text-slate-300 [&_td]:!text-slate-300 [&_th]:!text-white [&_summary]:!text-white [&_strong]:!text-white [&_pre]:!text-slate-200">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Data center hardware planning is a critical component of enterprise IT strategy. Poor capacity planning leads to underutilization, overprovisioning costs, or worse—system outages from insufficient resources. This guide covers the complete data center planning lifecycle from capacity assessment through procurement and deployment.
            </p>
          </section>

          {/* Capacity Modeling */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Center Capacity Modeling Framework</h2>
            <p className="text-gray-700 mb-6">
              Capacity planning starts with understanding current utilization and projecting future growth.
            </p>
            <div className="space-y-6 mb-8">
              {[
                {
                  step: '1. Inventory Current Infrastructure',
                  desc: 'Document existing servers, storage, network, power, and cooling capacity. Use monitoring tools to capture actual utilization (CPU, RAM, disk, network).',
                },
                {
                  step: '2. Identify Workload Growth',
                  desc: 'Project application growth, user growth, data growth over 3-5 years. Distinguish between steady-state and peak loads.',
                },
                {
                  step: '3. Calculate Headroom',
                  desc: 'Plan for 30-40% spare capacity to handle spikes and future growth without immediate re-procurement.',
                },
                {
                  step: '4. Model Hardware Requirements',
                  desc: 'Map workload requirements (CPU cores, RAM, storage, network) to specific hardware models.',
                },
                {
                  step: '5. Consider Power & Cooling',
                  desc: 'Data center power and cooling are often limiting factors. Calculate power draw (watts) and heat dissipation (BTU).',
                },
                {
                  step: '6. Plan Redundancy',
                  desc: 'Add 100% capacity for high-availability services. N+1 (spare server) or N+2 (two spares) depending on criticality.',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border-l-4 border-blue-500">
                  <h3 className="font-bold text-gray-900 mb-2">{item.step}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-slate-950 rounded-lg p-8 border border-white/10">
              <h3 className="font-bold text-gray-900 mb-6">Example: Capacity Calculation</h3>
              <p className="text-gray-700 mb-4">Imagine a 3-tier application (web, app, database) serving 10,000 users:</p>
              <div className="space-y-4">
                {[
                  {
                    tier: 'Web Tier',
                    current: '2 servers, 16 cores, 64GB RAM each',
                    growth: '+50% in 3 years',
                    requirement: '3 servers × 2 = 6 (redundancy)',
                    hardware: '6× 32-core, 256GB RAM servers',
                  },
                  {
                    tier: 'App Tier',
                    current: '4 servers, 32 cores, 128GB each',
                    growth: '+100% in 3 years',
                    requirement: '8 servers + 2 spare = 10',
                    hardware: '10× 64-core, 512GB RAM servers',
                  },
                  {
                    tier: 'Database',
                    current: '2×8TB storage, 256GB RAM',
                    growth: '+200% data growth',
                    requirement: '2×24TB primary + 24TB HA replica',
                    hardware: '2× high-memory DB servers, 48TB SAN',
                  },
                ].map((row, idx) => (
                  <div key={idx} className="bg-slate-900 rounded border border-white/10 p-4">
                    <h4 className="font-bold text-gray-900 mb-3">{row.tier}</h4>
                    <div className="space-y-2 text-gray-700 text-sm">
                      <div><span className="font-semibold">Current:</span> {row.current}</div>
                      <div><span className="font-semibold">3-Year Growth:</span> {row.growth}</div>
                      <div><span className="font-semibold">Requirement:</span> {row.requirement}</div>
                      <div><span className="font-semibold">Hardware:</span> {row.hardware}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Server Selection */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Center Server Selection</h2>
            <p className="text-gray-700 mb-6">
              Server procurement is the largest hardware expense. Understand the different form factors and performance levels.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-slate-950 rounded-lg p-6 border border-white/10">
                <h3 className="font-bold text-gray-900 mb-4">1U Rack Servers (Standard)</h3>
                <p className="text-gray-700 mb-4">Most common, balances density & cost. 1 "unit" of 1.75" height in a 42U rack.</p>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p>✓ <strong>Pros:</strong> Good density, cost-effective, available models</p>
                  <p>✗ <strong>Cons:</strong> Limited upgrade options</p>
                  <p>📊 <strong>Cost:</strong> $3K-8K per server</p>
                  <p>🎯 <strong>Use:</strong> General-purpose web/app servers</p>
                </div>
              </div>
              <div className="bg-slate-950 rounded-lg p-6 border border-white/10">
                <h3 className="font-bold text-gray-900 mb-4">2U Rack Servers (High-Performance)</h3>
                <p className="text-gray-700 mb-4">More power, more cooling, for demanding workloads.</p>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p>✓ <strong>Pros:</strong> More RAM/storage, better cooling</p>
                  <p>✗ <strong>Cons:</strong> Lower density, higher cost & power</p>
                  <p>📊 <strong>Cost:</strong> $8K-15K per server</p>
                  <p>🎯 <strong>Use:</strong> Databases, analytics, high-memory apps</p>
                </div>
              </div>
              <div className="bg-slate-950 rounded-lg p-6 border border-white/10">
                <h3 className="font-bold text-gray-900 mb-4">GPU/Accelerator Servers</h3>
                <p className="text-gray-700 mb-4">NVIDIA GPUs for AI/ML, analytics, rendering.</p>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p>✓ <strong>Pros:</strong> Massive parallel computation, ML acceleration</p>
                  <p>✗ <strong>Cons:</strong> Very expensive, specialized workloads only</p>
                  <p>📊 <strong>Cost:</strong> $15K-50K+ per server with GPUs</p>
                  <p>🎯 <strong>Use:</strong> AI/ML, scientific computing, rendering</p>
                </div>
              </div>
              <div className="bg-slate-950 rounded-lg p-6 border border-white/10">
                <h3 className="font-bold text-gray-900 mb-4">Blade Servers (Legacy)</h3>
                <p className="text-gray-700 mb-4">Compact but proprietary, declining in use.</p>
                <div className="space-y-2 text-gray-700 text-sm">
                  <p>✓ <strong>Pros:</strong> Maximum density, shared infrastructure</p>
                  <p>✗ <strong>Cons:</strong> Vendor lock-in, limited options</p>
                  <p>📊 <strong>Cost:</strong> High per unit</p>
                  <p>🎯 <strong>Use:</strong> Legacy systems, specific enterprise scenarios</p>
                </div>
              </div>
            </div>
          </section>

          {/* Storage Planning */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Storage Infrastructure Planning</h2>
            <p className="text-gray-700 mb-6">
              Storage is often the bottleneck in capacity planning. Consider performance, capacity, and redundancy.
            </p>
            <div className="space-y-6 mb-8">
              {[
                {
                  storage: 'SAN (Storage Area Network)',
                  tech: 'Fibre Channel or iSCSI, centralized block storage',
                  perf: 'Very high (3,000-15,000 IOPS)',
                  capacity: '100TB-100PB+',
                  cost: '$30K-500K+ depending on performance/redundancy',
                  use: 'Databases, high-performance applications',
                  pros: ['Multiple server access', 'Snapshots/replication', 'Enterprise SLAs'],
                  cons: ['Complex setup', 'High cost', 'Requires expertise'],
                },
                {
                  storage: 'NAS (Network Attached Storage)',
                  tech: 'File-based over Ethernet (NFS, SMB)',
                  perf: 'Moderate (1,000-5,000 IOPS)',
                  capacity: '50TB-500TB',
                  cost: '$5K-50K',
                  use: 'File servers, shared content, backups',
                  pros: ['Easier to manage', 'File-level sharing', 'Lower cost'],
                  cons: ['Network latency', 'Not suitable for DB', 'Less scalable'],
                },
                {
                  storage: 'Direct Attached Storage (DAS)',
                  tech: 'Local drives in server (SAS, NVMe)',
                  perf: 'Very high (10,000+ IOPS with NVMe)',
                  capacity: '10TB-200TB per server',
                  cost: '$2K-10K per server',
                  use: 'Database nodes, cache layers, high-I/O apps',
                  pros: ['Lowest latency', 'Highest performance', 'Simple'],
                  cons: ['No replication', 'Not shared', 'Limited capacity'],
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border-l-4 border-purple-500">
                  <h3 className="font-bold text-gray-900 mb-2 text-lg">{item.storage}</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4 text-gray-700 text-sm">
                    <div>
                      <p><span className="font-semibold">Technology:</span> {item.tech}</p>
                      <p><span className="font-semibold">Performance:</span> {item.perf}</p>
                      <p><span className="font-semibold">Capacity:</span> {item.capacity}</p>
                    </div>
                    <div>
                      <p><span className="font-semibold">Cost:</span> {item.cost}</p>
                      <p><span className="font-semibold">Best for:</span> {item.use}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-700">
                    <div><span className="font-semibold text-green-700">✓ Pros:</span> {item.pros.join(', ')}</div>
                    <div><span className="font-semibold text-orange-700">⚠ Cons:</span> {item.cons.join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Networking */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Network Infrastructure Planning</h2>
            <p className="text-gray-700 mb-6">
              Network bandwidth is critical. Plan for today's traffic plus growth.
            </p>
            <div className="bg-slate-950 rounded-lg p-6 border border-white/10 mb-8">
              <h3 className="font-bold text-gray-900 mb-4">Network Bandwidth Calculation</h3>
              <p className="text-gray-700 mb-4">Example: 10,000 users, 100 requests/user/day, 100KB per request</p>
              <div className="bg-slate-900 border border-white/10 rounded p-4 font-mono text-sm mb-4 overflow-x-auto text-slate-200">
                <pre>{`Daily traffic = 10,000 users × 100 requests × 100KB
           = 100,000,000 KB = 100GB per day
           
Sustained rate (8 hours peak) = 100GB ÷ 8hrs ÷ 3,600sec
                               ≈ 3.5 Mbps average
                               
Peak rate (assume 10× peak) = 35 Mbps

Recommended network = 100 Mbps (with headroom)
Future-proof = 1 Gbps uplink`}</pre>
              </div>
              <table className="w-full">
                <thead className="bg-slate-900">
                  <tr>
                    <th className="px-4 py-2 text-left">Component</th>
                    <th className="px-4 py-2 text-left">Requirement</th>
                    <th className="px-4 py-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Uplink bandwidth', '100 Mbps - 10 Gbps', 'Internet connectivity'],
                    ['Internal switching', '10 Gbps', 'Inter-server traffic'],
                    ['Server NICs', '1-10 Gbps', 'Per-server network adapter'],
                    ['Network redundancy', 'Dual links with failover', 'No single point of failure'],
                    ['QoS / Traffic shaping', 'Prioritize critical traffic', 'Ensure responsiveness'],
                  ].map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-black' : 'bg-slate-950'}>
                      <td className="px-4 py-2 font-semibold">{row[0]}</td>
                      <td className="px-4 py-2">{row[1]}</td>
                      <td className="px-4 py-2 text-gray-600">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Power & Cooling */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Power & Cooling Considerations</h2>
            <p className="text-gray-700 mb-6">
              Data center power and cooling often limit capacity before actual compute resources.
            </p>
            <div className="space-y-4">
              {[
                {
                  factor: 'Power Consumption',
                  consideration: 'Modern servers: 300-800W per 1U unit. Calculate total watts needed and ensure facility power available.',
                },
                {
                  factor: 'Power Distribution',
                  consideration: 'Plan for redundant PDUs (Power Distribution Units), UPS backup, and generator capacity.',
                },
                {
                  factor: 'Cooling Capacity',
                  consideration: 'Data centers need 1.5-2.5kW of cooling per 1kW of IT load (depends on architecture). Hot/cold aisle containment recommended.',
                },
                {
                  factor: 'PUE (Power Usage Effectiveness)',
                  consideration: 'Ratio of total facility power to IT power. Modern: 1.2-1.5. Legacy: 2.0+. Lower is better.',
                },
                {
                  factor: 'Rack Density',
                  consideration: 'Don\'t exceed 8-10kW per rack in standard cooling. Use liquid cooling for higher densities.',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border-l-4 border-orange-500">
                  <h4 className="font-bold text-gray-900 mb-2">{item.factor}</h4>
                  <p className="text-gray-700">{item.consideration}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Procurement Strategy */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Center Hardware Procurement Strategy</h2>
            <div className="space-y-6">
              {[
                {
                  strategy: 'Staggered Procurement (Recommended)',
                  desc: 'Buy hardware in phases as capacity is needed. Improves technology freshness, spreads costs, reduces holding costs.',
                  pros: ['Cost-effective', 'Technology refresh', 'Flexible'],
                  cons: ['Longer lead times', 'Different hardware revisions'],
                  timeline: 'Wave 1 (Year 1), Wave 2 (Year 2), Wave 3+ (Year 3+)',
                },
                {
                  strategy: 'Bulk Procurement',
                  desc: 'Buy entire 3-5 year capacity upfront. Better negotiating power, all hardware equivalent, simpler deployment.',
                  pros: ['Volume discounts (30-40%)', 'Homogenous fleet', 'Faster deployment'],
                  cons: ['Higher CapEx', 'Technology older by year 3-5', 'Risk of overprovisioning'],
                  timeline: 'Single large purchase',
                },
                {
                  strategy: 'Vendor Refresh Programs',
                  desc: 'Regular equipment swaps with vendor (leasing vs buying). Newest tech, vendor maintains equipment.',
                  pros: ['Always current hardware', 'Vendor support', 'Fixed OpEx'],
                  cons: ['Higher cost over time', 'Vendor dependence'],
                  timeline: '3-year leases with refresh cycles',
                },
                {
                  strategy: 'Cloud Burst / Hybrid',
                  desc: 'Keep core on-premises, use cloud for peak demand. Reduces on-prem capacity & cost.',
                  pros: ['Lower CapEx', 'Flexible scaling', 'Cost optimization'],
                  cons: ['Hybrid complexity', 'Network costs', 'Vendor lock-in'],
                  timeline: 'Hybrid deployment',
                },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-950 rounded-lg p-6 border-l-4 border-blue-500">
                  <h3 className="font-bold text-gray-900 mb-2">{item.strategy}</h3>
                  <p className="text-gray-700 mb-3">{item.desc}</p>
                  <div className="grid md:grid-cols-2 gap-4 mb-3 text-sm text-gray-700">
                    <div><span className="font-semibold text-green-700">✓ {item.pros.join(', ')}</span></div>
                    <div><span className="font-semibold text-orange-700">⚠ {item.cons.join(', ')}</span></div>
                  </div>
                  <p className="text-gray-600 italic text-sm">Timeline: {item.timeline}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Colocation */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Colocation vs On-Premises Data Center</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-950 rounded-lg p-6 border border-blue-500/30">
                <h3 className="font-bold text-gray-900 mb-4">Colocation (Co-lo)</h3>
                <p className="text-gray-700 mb-4">Rent physical space from a data center provider (Equinix, CoreWeave, ViaWest, etc). You own hardware, provider provides space/power/network.</p>
                <p className="font-semibold text-gray-900 mb-3">Cost Model:</p>
                <ul className="space-y-2 text-gray-700 text-sm mb-4">
                  <li>• Rack space: $300-1,000/month per rack</li>
                  <li>• Power: $100-500/month per kW</li>
                  <li>• Bandwidth: $100-1,000/month per 1Gbps</li>
                  <li>• Hardware: Your capital investment</li>
                </ul>
                <p className="font-semibold text-gray-900 mb-3">Best for:</p>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>✓ Organizations with existing hardware</li>
                  <li>✓ High-value hardware (GPUs, specialized)</li>
                  <li>✓ Need control over infrastructure</li>
                  <li>✓ Large capacity requirements</li>
                </ul>
              </div>
              <div className="bg-slate-950 rounded-lg p-6 border border-green-500/30">
                <h3 className="font-bold text-gray-900 mb-4">On-Premises Data Center</h3>
                <p className="text-gray-700 mb-4">Own and operate your own facility. Requires capital investment in real estate, cooling, power distribution, security.</p>
                <p className="font-semibold text-gray-900 mb-3">Cost Model:</p>
                <ul className="space-y-2 text-gray-700 text-sm mb-4">
                  <li>• Real estate: $50-300/sqft/year</li>
                  <li>• Power infrastructure: $500K-5M+</li>
                  <li>• Cooling: $200K-1M+</li>
                  <li>• Operations staff: $500K-2M+/year</li>
                </ul>
                <p className="font-semibold text-gray-900 mb-3">Best for:</p>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>✓ Very large organizations</li>
                  <li>✓ Strict compliance/data residency</li>
                  <li>✓ 10+ years planned usage</li>
                  <li>✓ Significant economies of scale</li>
                </ul>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Data Center Planning FAQs</h2>
            <div className="space-y-4">
              {[
                {
                  q: 'How much spare capacity should we plan for?',
                  a: '30-40% for normal growth. 50% for high-growth environments. Pure redundancy (N+1 or N+2) adds additional capacity.',
                },
                {
                  q: 'Should we replace hardware every 3 or 5 years?',
                  a: '5-year refresh is typical sweet spot. Older hardware still functional but increases energy costs and reliability risk.',
                },
                {
                  q: 'How do we handle data growth (storage capacity)?',
                  a: 'Plan for exponential growth (2× data every 2-3 years is common). Build in 200% growth capacity for databases.',
                },
                {
                  q: 'What redundancy level do we need?',
                  a: 'Critical systems: N+1 (spare capacity). Very critical: N+2. Standard: N (no redundancy, rely on clustering). Depends on RTO/RPO requirements.',
                },
              ].map((item, idx) => (
                <details key={idx} className="group bg-slate-950 border border-white/10 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow">
                  <summary className="flex justify-between items-center font-semibold text-gray-900">
                    {item.q}
                    <span className="text-accent">+</span>
                  </summary>
                  <p className="mt-4 text-gray-600">{item.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-accent to-accent/80 text-black rounded-lg p-10 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to Plan Your Data Center Infrastructure?</h2>
            <p className="text-lg mb-6 text-black/80">
              Let our infrastructure experts help you design a scalable, cost-effective data center strategy.
            </p>
            <Link
              to="/request-hardware-quote"
              className="inline-flex items-center bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-black/90 transition-all"
            >
              Get Infrastructure Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </section>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-8">Related Articles</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Enterprise Hardware Procurement Planning',
                link: '/blog/how-to-plan-enterprise-hardware-procurement',
              },
              {
                title: 'Enterprise Cloud Migration Strategy',
                link: '/blog/enterprise-cloud-migration-strategy',
              },
            ].map((article, idx) => (
              <Link
                key={idx}
                to={article.link}
                className="group bg-gray-900 border border-gray-700 rounded-lg p-6 hover:shadow-lg transition-all"
              >
                <h4 className="font-bold text-white group-hover:text-accent transition-colors">{article.title}</h4>
                <p className="text-accent text-sm mt-3 flex items-center gap-1">
                  Read More <ArrowRight className="w-3 h-3" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
